import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLoader } from './components/TableShared';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Bookings from './pages/Bookings';
import Sitters from './pages/Sitters';
import Reviews from './pages/Reviews';
import UserReports from './pages/UserReports';
import Withdrawals from './pages/Withdrawals';
import PayoutSettings from './pages/PayoutSettings';
import AppVersionSettings from './pages/AppVersionSettings';
import AppConfig from './pages/AppConfig';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Pets from './pages/Pets';
import Services from './pages/Services';
import Transactions from './pages/Transactions';
import Chats from './pages/Chats';
import LinkedQR from './pages/LinkedQR';
import QRCodes from './pages/QRCodes';
import Reports from './pages/Reports';
import Coins from './pages/Coins';
import FeatureFlags from './pages/FeatureFlags';
import PublicTagPage from './pages/PublicTagPage';
import LegalPage from './pages/legal/LegalPage';
import api from './config/api';

// Public, auth-bypassed info/legal pages (linked from the mobile app).
const LEGAL_ROUTES = ['privacy-policy', 'terms-of-service', 'about', 'contact', 'delete-account'];

function App() {
  // Public (auth-bypassed) routes take precedence over the admin shell
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  if (pathname.startsWith('/tag/')) {
    return (
      <Routes>
        <Route path="/tag/:code" element={<PublicTagPage />} />
      </Routes>
    );
  }

  const legalSlug = pathname.replace(/^\/+|\/+$/g, '');
  if (LEGAL_ROUTES.includes(legalSlug)) {
    return (
      <Routes>
        {LEGAL_ROUTES.map((slug) => (
          <Route key={slug} path={`/${slug}`} element={<LegalPage slug={slug} />} />
        ))}
      </Routes>
    );
  }

  return <AdminShell />;
}

function AdminShell() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      // Verify token is still valid and user is admin
      api.get('/auth/verify-token')
        .then((res) => {
          const user = res.data?.data?.user;
          if (user && user.role === 'ADMIN') {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('adminToken');
            setIsAuthenticated(false);
          }
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          setIsAuthenticated(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for auth-expired events dispatched by the API interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <AppLoader />
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Layout onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/users" element={<Users />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/sitters" element={<Sitters />} />
        <Route path="/pets" element={<Pets />} />
        <Route path="/services" element={<Services />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/chats" element={<Chats />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/user-reports" element={<UserReports />} />
        <Route path="/withdrawals" element={<Withdrawals />} />
        <Route path="/payout-settings" element={<PayoutSettings />} />
        <Route path="/app-version" element={<AppVersionSettings />} />
        <Route path="/app-config" element={<AppConfig />} />
        <Route path="/linked-qr" element={<LinkedQR />} />
        <Route path="/qr-codes" element={<QRCodes />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/coins" element={<Coins />} />
        <Route path="/feature-flags" element={<FeatureFlags />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}

export default App;
